import React, {useEffect, useRef, useState} from 'react';
import Transition from '../Transition';
import gen_open_ai_resp from './open-ai';
import {ICONS} from '../../Constants/Icons';
import AppLoader from '../../HelperClasses/AppLoaderClasses/AppLoader';
import Markdown from 'markdown-to-jsx';
import SecondaryButton from '../../UI/Buttons/SecondaryButton';
import ToastPopup from '../../HelperClasses/AppLoaderClasses/ToastPopup';
import {ToastContainer} from 'react-toastify';
import {aiGenerateMixPanelEvent} from '../../Models/MixPannelEnum';
import {useLocale} from '../../Localization/LocaleContext';

const GenerateText = ({
  system_instruction,
  user_id,
  user_message,
  openSide = 'left',
  disabled = false,
  onDisabledAlert,
  isShowCopyButton = false,
  type,
}) => {
  const {translate: t} = useLocale();
  const [open_form, setOpenForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ai_instructions, setAIInstructions] = useState({
    system_instruction: system_instruction,
    user_message: user_message,
    user_id: user_id,
    model: 'gpt-6-luna',
    max_tokens: 5000,
  });
  const [ai_resp, setAIResp] = useState(null);

  //

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({keyCode}) => {
      if (!open_form || keyCode !== 27) return;
      setAIInstructions({
        system_instruction: system_instruction,
        user_message: user_message,
        user_id: user_id,
        model: 'gpt-6-luna',
        max_tokens: 5000,
      });
      setOpenForm(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  //

  useEffect(() => {
    function handleClickOutside(event) {
      if (trigger.current && !trigger.current.contains(event.target)) {
        // setOpenSide('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [trigger]);

  // useEffect to check if the user_message is empty
  useEffect(() => {
    setAIInstructions({
      ...ai_instructions,
      user_message: user_message,
      system_instruction: system_instruction,
    });
  }, [user_message, system_instruction]);

  const onClickGenerate = async () => {
    setIsLoading(true);
    const resp = await gen_open_ai_resp(ai_instructions);
    setIsLoading(false);
    setAIResp(resp);
    setIsLoading(false);
    if (type) {
      aiGenerateMixPanelEvent(type);
    }
  };

  const onClickCopy = () => {
    if (!ai_resp?.ai_response) {
      ToastPopup({
        type: 'error',
        message: t('common.ai_no_response_to_copy', 'No response to copy'),
      });
      return;
    }
    navigator.clipboard.writeText(ai_resp?.ai_response);
    ToastPopup({
      type: 'success',
      message: t('common.copied_to_clipboard', 'Copied to clipboard'),
    });
  };

  return (
    <>
      <div className={'  inline-flex '}>
        <button
          ref={trigger}
          className=" text-gray-400"
          onClick={() => {
            if (disabled) {
              onDisabledAlert();
              return;
            }
            onClickGenerate();
            setOpenForm(!open_form);
          }}>
          {ICONS.ai_star}
        </button>

        {/*  */}
        <Transition
          show={open_form}
          tag="div"
          className={`origin-top-right z-10 absolute top-full  w-[70vw]  md:w-[450px]  bg-white border border-gray-200  rounded shadow-lg overflow-hidden mt-1 ${
            openSide === 'right' ? 'right-0' : 'left-0'
          }`}
          enter="transition ease-out duration-200 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0">
          {' '}
          <div className="  flex flex-col" ref={dropdown}>
            <div className=" px-4 py-2.5 bg-gray-100 flex items-center justify-between ">
              <p className=" text-sm font-semibold text-black">{t('common.ai_suggestions', 'Suggestions')}</p>

              {/*  */}
              <div className=" flex item gap-4">
                {isShowCopyButton && (
                  <SecondaryButton
                    onClick={() => onClickCopy()}
                    className=" !py-1 !px-2 !text-sm">
                    {t('common.copy', 'Copy')}
                  </SecondaryButton>
                )}
                {!isLoading && (
                  <SecondaryButton
                    onClick={() => onClickGenerate()}
                    className=" !py-1 !px-2 !text-sm">
                    {t('common.regenerate', 'Regenerate')}
                  </SecondaryButton>
                )}
                {/* close button */}
                <button
                  className={'    flex flex-col  items-center justify-center  '}
                  onClick={() => {
                    setAIInstructions({
                      system_instruction: system_instruction,
                      user_message: user_message,
                      user_id: user_id,
                      model: 'gpt-6-luna',
                      max_tokens: 5000,
                    });
                    setAIResp(null);
                    setOpenForm(!open_form);
                  }}>
                  {ICONS.close}
                  <span className=" text-xs  font-light">{t('common.esc', 'ESC')}</span>
                </button>
              </div>
            </div>

            {isLoading && (
              <div className="   p-4  animate-pulse w-full space-y-2">
                <span className=" block w-full h-3 bg-[#3B3269] bg-opacity-[20%]  rounded-md" />
                <span className=" block w-full h-3 bg-[#3B3269] bg-opacity-[20%]  rounded-md" />
                <span className=" block w-full h-3 bg-[#3B3269] bg-opacity-[20%]  rounded-md" />
                <span className=" block w-full h-3 bg-[#3B3269] bg-opacity-[20%]  rounded-md" />
                <span className=" block w-full h-3 bg-[#3B3269] bg-opacity-[20%]  rounded-md" />
              </div>
            )}
            {ai_resp?.ai_response && !isLoading && (
              <div className={['p-4 max-h-[500px] overflow-y-auto  prose']}>
                <Markdown>{ai_resp?.ai_response}</Markdown>
              </div>
            )}
          </div>
        </Transition>
      </div>

      <ToastContainer />
    </>
  );
};

export default GenerateText;
